import React, { FC, Fragment, useEffect, useMemo, useState } from 'react';
import {
  BaseModal,
  Button,
  ToastMessage,
  Dropdown,
  DropdownList,
  DropdownItem,
  Textarea,
  Multiselect
} from '@cokitchen/cokitchen-components';
import { AddIcon, ArrowDownIcon, NotificationIcon } from 'cokitchen-icons';
import { IssueType } from 'models/issue.model';
import { MultiselectItem } from '@cokitchen/cokitchen-components/dist/form/multiselect/types';
import {
  ENUM_ESCALATE_ITEMS,
  ENUM_ISSUE_DESTINATION,
  ENUM_ISSUE_RESOLUTION,
  ENUM_ISSUE_SUMMARY,
  ENUM_ITEM_TYPE,
  ENUM_ORDER_PROVIDER
} from 'utils/enum';
import { apiService } from 'utils/api';
import { toast } from 'react-toastify';
import { OrderType } from 'models/order.model';
import { RiderType } from 'models/rider.model';
import useDashboardContext from 'contexts/dashboard.context';
import { UserType } from 'models/user.model';
import { getUserName } from 'utils/functions';

type Props = {
  onCreateNewIssue?: (issue: IssueType) => void;
};

type FormData = {
  user_id?: string;
  order_code?: string;
  channel: string;
  feedback_destination: string;
  feedback_destination_other?: string;
  feedback_summary: string;
  feedback_summary_other?: string;
  case_summary?: string;
  brands?: { id: string; name: string }[];
  rider_id?: string | null;
  resolution?: string;
  resolution_other?: string;
};

const CHANNEL_OF_ESCALATION: MultiselectItem[] = [
  {
    text: ENUM_ESCALATE_ITEMS.EMAIL,
    value: ENUM_ESCALATE_ITEMS.EMAIL
  },
  {
    text: ENUM_ESCALATE_ITEMS.INSTAGRAM,
    value: ENUM_ESCALATE_ITEMS.INSTAGRAM
  },
  {
    text: ENUM_ESCALATE_ITEMS.APP_FEEDBACK,
    value: ENUM_ESCALATE_ITEMS.APP_FEEDBACK
  },
  {
    text: ENUM_ESCALATE_ITEMS.PHONE_CALL,
    value: ENUM_ESCALATE_ITEMS.PHONE_CALL
  },
  {
    text: ENUM_ESCALATE_ITEMS.TWITTER,
    value: ENUM_ESCALATE_ITEMS.TWITTER
  },
  {
    text: ENUM_ESCALATE_ITEMS.WHATSAPP,
    value: ENUM_ESCALATE_ITEMS.WHATSAPP
  },
  {
    text: ENUM_ESCALATE_ITEMS.INBOUND,
    value: ENUM_ESCALATE_ITEMS.INBOUND
  },
  {
    text: ENUM_ESCALATE_ITEMS.OUTBOUND,
    value: ENUM_ESCALATE_ITEMS.OUTBOUND
  }
];

const FEEDBACK_DESTINATION: MultiselectItem[] = [
  {
    text: ENUM_ISSUE_DESTINATION.KITCHEN,
    value: ENUM_ISSUE_DESTINATION.KITCHEN
  },
  {
    text: ENUM_ISSUE_DESTINATION.LOGISTICS,
    value: ENUM_ISSUE_DESTINATION.LOGISTICS
  },
  {
    text: ENUM_ISSUE_DESTINATION.TECH_TEAM,
    value: ENUM_ISSUE_DESTINATION.TECH_TEAM
  },
  {
    text: ENUM_ISSUE_DESTINATION.BANK_AND_FINANCE,
    value: ENUM_ISSUE_DESTINATION.BANK_AND_FINANCE
  },
  {
    text: ENUM_ISSUE_DESTINATION.CUSTOMER_SERVICE,
    value: ENUM_ISSUE_DESTINATION.CUSTOMER_SERVICE
  },
  {
    text: ENUM_ISSUE_DESTINATION.HOAX,
    value: ENUM_ISSUE_DESTINATION.HOAX
  },
  {
    text: ENUM_ISSUE_DESTINATION.DESTINATION_OTHER,
    value: ENUM_ISSUE_DESTINATION.DESTINATION_OTHER
  }
];

const ISSUE_RESOLUTION: MultiselectItem[] = [
  {
    text: ENUM_ISSUE_RESOLUTION.BANK_REFUND,
    value: ENUM_ISSUE_RESOLUTION.BANK_REFUND
  },
  {
    text: ENUM_ISSUE_RESOLUTION.DELIVERED,
    value: ENUM_ISSUE_RESOLUTION.DELIVERED
  },
  {
    text: ENUM_ISSUE_RESOLUTION.MEAL_REPACKAGED,
    value: ENUM_ISSUE_RESOLUTION.MEAL_REPACKAGED
  },
  {
    text: ENUM_ISSUE_RESOLUTION.MEAL_REPLACEMENT,
    value: ENUM_ISSUE_RESOLUTION.MEAL_REPLACEMENT
  },
  {
    text: ENUM_ISSUE_RESOLUTION.ORDER_CANCELLED,
    value: ENUM_ISSUE_RESOLUTION.ORDER_CANCELLED
  },
  {
    text: ENUM_ISSUE_RESOLUTION.ORDER_RECALLED,
    value: ENUM_ISSUE_RESOLUTION.ORDER_RECALLED
  },
  {
    text: ENUM_ISSUE_RESOLUTION.PENDING,
    value: ENUM_ISSUE_RESOLUTION.PENDING
  },
  {
    text: ENUM_ISSUE_RESOLUTION.USER_EDUCATION,
    value: ENUM_ISSUE_RESOLUTION.USER_EDUCATION
  },
  {
    text: ENUM_ISSUE_RESOLUTION.USER_PACIFIED,
    value: ENUM_ISSUE_RESOLUTION.USER_PACIFIED
  },
  {
    text: ENUM_ISSUE_RESOLUTION.WALLET_REFUND,
    value: ENUM_ISSUE_RESOLUTION.WALLET_REFUND
  },
  {
    text: ENUM_ISSUE_RESOLUTION.RESOLUTION_OTHER,
    value: ENUM_ISSUE_RESOLUTION.RESOLUTION_OTHER
  }
];

const ISSUE_SUMMARY: Array<MultiselectItem & { show: ENUM_ISSUE_DESTINATION[] }> = [
  {
    text: ENUM_ISSUE_SUMMARY.PAYMENT_CONFIRMATION_DELAY,
    value: ENUM_ISSUE_SUMMARY.PAYMENT_CONFIRMATION_DELAY,
    show: [ENUM_ISSUE_DESTINATION.BANK_AND_FINANCE]
  },
  {
    text: ENUM_ISSUE_SUMMARY.DELAYED_PREPARATION,
    value: ENUM_ISSUE_SUMMARY.DELAYED_PREPARATION,
    show: [ENUM_ISSUE_DESTINATION.KITCHEN, ENUM_ISSUE_DESTINATION.BANK_AND_FINANCE]
  },
  {
    text: ENUM_ISSUE_SUMMARY.PORTIONING,
    value: ENUM_ISSUE_SUMMARY.PORTIONING,
    show: [ENUM_ISSUE_DESTINATION.KITCHEN]
  },
  {
    text: ENUM_ISSUE_SUMMARY.APP_ACCESSIBILITY,
    value: ENUM_ISSUE_SUMMARY.APP_ACCESSIBILITY,
    show: [ENUM_ISSUE_DESTINATION.TECH_TEAM, ENUM_ISSUE_DESTINATION.CUSTOMER_SERVICE]
  },
  {
    text: ENUM_ISSUE_SUMMARY.PAYSTACK_ORDER_NOT_CREATED,
    value: ENUM_ISSUE_SUMMARY.PAYSTACK_ORDER_NOT_CREATED,
    show: [ENUM_ISSUE_DESTINATION.TECH_TEAM, ENUM_ISSUE_DESTINATION.CUSTOMER_SERVICE]
  },
  {
    text: ENUM_ISSUE_SUMMARY.DRINK_QUALITY_TASTE,
    value: ENUM_ISSUE_SUMMARY.DRINK_QUALITY_TASTE,
    show: [ENUM_ISSUE_DESTINATION.KITCHEN, ENUM_ISSUE_DESTINATION.HOAX]
  },
  {
    text: ENUM_ISSUE_SUMMARY.LATE_DELIVERY,
    value: ENUM_ISSUE_SUMMARY.LATE_DELIVERY,
    show: [ENUM_ISSUE_DESTINATION.LOGISTICS]
  },
  {
    text: ENUM_ISSUE_SUMMARY.NIGHT_POLICY,
    value: ENUM_ISSUE_SUMMARY.NIGHT_POLICY,
    show: [ENUM_ISSUE_DESTINATION.LOGISTICS, ENUM_ISSUE_DESTINATION.HOAX]
  },
  {
    text: ENUM_ISSUE_SUMMARY.INCOMPLETE_ORDER_MISSING_ITEM,
    value: ENUM_ISSUE_SUMMARY.INCOMPLETE_ORDER_MISSING_ITEM,
    show: [
      ENUM_ISSUE_DESTINATION.KITCHEN,
      ENUM_ISSUE_DESTINATION.LOGISTICS,
      ENUM_ISSUE_DESTINATION.HOAX
    ]
  },
  {
    text: ENUM_ISSUE_SUMMARY.DRINK_NOT_DELIVERED,
    value: ENUM_ISSUE_SUMMARY.DRINK_NOT_DELIVERED,
    show: [ENUM_ISSUE_DESTINATION.LOGISTICS]
  },
  {
    text: ENUM_ISSUE_SUMMARY.ITEM_NOT_PACKED,
    value: ENUM_ISSUE_SUMMARY.ITEM_NOT_PACKED,
    show: [ENUM_ISSUE_DESTINATION.KITCHEN]
  },
  {
    text: ENUM_ISSUE_SUMMARY.WRONG_ORDER_DELIVERED,
    value: ENUM_ISSUE_SUMMARY.WRONG_ORDER_DELIVERED,
    show: [ENUM_ISSUE_DESTINATION.LOGISTICS]
  },
  {
    text: ENUM_ISSUE_SUMMARY.WRONG_ORDER_PACKED,
    value: ENUM_ISSUE_SUMMARY.WRONG_ORDER_PACKED,
    show: [ENUM_ISSUE_DESTINATION.KITCHEN]
  },
  {
    text: ENUM_ISSUE_SUMMARY.RUDE_RIDER,
    value: ENUM_ISSUE_SUMMARY.RUDE_RIDER,
    show: [ENUM_ISSUE_DESTINATION.LOGISTICS]
  },
  {
    text: ENUM_ISSUE_SUMMARY.RIDER_NOT_PICKING_CALLS,
    value: ENUM_ISSUE_SUMMARY.RIDER_NOT_PICKING_CALLS,
    show: [ENUM_ISSUE_DESTINATION.LOGISTICS]
  },
  {
    text: ENUM_ISSUE_SUMMARY.CS_ISSUES_RUDENESS_UNWILLINGNESS_TO_ASSIST,
    value: ENUM_ISSUE_SUMMARY.CS_ISSUES_RUDENESS_UNWILLINGNESS_TO_ASSIST,
    show: [ENUM_ISSUE_DESTINATION.LOGISTICS, ENUM_ISSUE_DESTINATION.CUSTOMER_SERVICE]
  },
  {
    text: ENUM_ISSUE_SUMMARY.SPILLED_MEAL,
    value: ENUM_ISSUE_SUMMARY.SPILLED_MEAL,
    show: [ENUM_ISSUE_DESTINATION.KITCHEN, ENUM_ISSUE_DESTINATION.LOGISTICS]
  },
  {
    text: ENUM_ISSUE_SUMMARY.DOUBLE_CARD_PAYMENT,
    value: ENUM_ISSUE_SUMMARY.DOUBLE_CARD_PAYMENT,
    show: [ENUM_ISSUE_DESTINATION.TECH_TEAM]
  },
  {
    text: ENUM_ISSUE_SUMMARY.TRANSFER_ISSUES_NON_RECEIPT_OF_FUNDS_DOUBLE_TRANSFER_ETC,
    value: ENUM_ISSUE_SUMMARY.TRANSFER_ISSUES_NON_RECEIPT_OF_FUNDS_DOUBLE_TRANSFER_ETC,
    show: [ENUM_ISSUE_DESTINATION.BANK_AND_FINANCE]
  },
  {
    text: ENUM_ISSUE_SUMMARY.FOOD_QUALITY_TASTE,
    value: ENUM_ISSUE_SUMMARY.FOOD_QUALITY_TASTE,
    show: [ENUM_ISSUE_DESTINATION.KITCHEN]
  },
  {
    text: ENUM_ISSUE_SUMMARY.ORDER_STATUS_KITCHEN,
    value: ENUM_ISSUE_SUMMARY.ORDER_STATUS_KITCHEN,
    show: [ENUM_ISSUE_DESTINATION.KITCHEN]
  },
  {
    text: ENUM_ISSUE_SUMMARY.ORDER_STATUS_LOGISTICS,
    value: ENUM_ISSUE_SUMMARY.ORDER_STATUS_LOGISTICS,
    show: [ENUM_ISSUE_DESTINATION.LOGISTICS]
  },
  {
    text: ENUM_ISSUE_SUMMARY.REFUND_ISSUE_WALLET,
    value: ENUM_ISSUE_SUMMARY.REFUND_ISSUE_WALLET,
    show: [ENUM_ISSUE_DESTINATION.CUSTOMER_SERVICE]
  },
  {
    text: ENUM_ISSUE_SUMMARY.REFUND_ISSUE_BANK,
    value: ENUM_ISSUE_SUMMARY.REFUND_ISSUE_BANK,
    show: [ENUM_ISSUE_DESTINATION.CUSTOMER_SERVICE]
  },
  {
    text: ENUM_ISSUE_SUMMARY.MEAL_AVAILABILITY,
    value: ENUM_ISSUE_SUMMARY.MEAL_AVAILABILITY,
    show: [ENUM_ISSUE_DESTINATION.KITCHEN]
  },
  {
    text: ENUM_ISSUE_SUMMARY.ORDER_COMPLETED_BUT_NOT_YET_DELIVERED,
    value: ENUM_ISSUE_SUMMARY.ORDER_COMPLETED_BUT_NOT_YET_DELIVERED,
    show: [ENUM_ISSUE_DESTINATION.LOGISTICS]
  },
  {
    text: ENUM_ISSUE_SUMMARY.OTP_ISSUE,
    value: ENUM_ISSUE_SUMMARY.OTP_ISSUE,
    show: [ENUM_ISSUE_DESTINATION.TECH_TEAM]
  },
  {
    text: ENUM_ISSUE_SUMMARY.USER_EDUCATION_ON_MEAL,
    value: ENUM_ISSUE_SUMMARY.USER_EDUCATION_ON_MEAL,
    show: [ENUM_ISSUE_DESTINATION.CUSTOMER_SERVICE, ENUM_ISSUE_DESTINATION.HOAX]
  },
  {
    text: ENUM_ISSUE_SUMMARY.ADDRESS_UPDATE,
    value: ENUM_ISSUE_SUMMARY.ADDRESS_UPDATE,
    show: [ENUM_ISSUE_DESTINATION.LOGISTICS]
  },
  {
    text: ENUM_ISSUE_SUMMARY.DELIVERY_REQUEST,
    value: ENUM_ISSUE_SUMMARY.DELIVERY_REQUEST,
    show: [ENUM_ISSUE_DESTINATION.LOGISTICS, ENUM_ISSUE_DESTINATION.HOAX]
  },
  {
    text: ENUM_ISSUE_SUMMARY.RECALLED_ORDER,
    value: ENUM_ISSUE_SUMMARY.RECALLED_ORDER,
    show: [ENUM_ISSUE_DESTINATION.KITCHEN, ENUM_ISSUE_DESTINATION.LOGISTICS]
  },
  {
    text: ENUM_ISSUE_SUMMARY.SOLD_OUT_ITEM,
    value: ENUM_ISSUE_SUMMARY.SOLD_OUT_ITEM,
    show: [ENUM_ISSUE_DESTINATION.KITCHEN]
  },
  {
    text: ENUM_ISSUE_SUMMARY.PRICE_DISPARITY_ON_APP_ORDERED_ITEM,
    value: ENUM_ISSUE_SUMMARY.PRICE_DISPARITY_ON_APP_ORDERED_ITEM,
    show: [ENUM_ISSUE_DESTINATION.TECH_TEAM]
  },
  {
    text: ENUM_ISSUE_SUMMARY.WRONG_LOCATION_APP_RELATED,
    value: ENUM_ISSUE_SUMMARY.WRONG_LOCATION_APP_RELATED,
    show: [ENUM_ISSUE_DESTINATION.LOGISTICS, ENUM_ISSUE_DESTINATION.TECH_TEAM]
  },
  {
    text: ENUM_ISSUE_SUMMARY.KITCHEN_TOOK_TOO_LONG_TO_ACCEPT,
    value: ENUM_ISSUE_SUMMARY.KITCHEN_TOOK_TOO_LONG_TO_ACCEPT,
    show: [ENUM_ISSUE_DESTINATION.KITCHEN]
  },
  {
    text: ENUM_ISSUE_SUMMARY.LOGISTICS_TOOK_TIME_TO_ASSIGN_DISPATCH_ORDER,
    value: ENUM_ISSUE_SUMMARY.LOGISTICS_TOOK_TIME_TO_ASSIGN_DISPATCH_ORDER,
    show: [ENUM_ISSUE_DESTINATION.LOGISTICS]
  },
  {
    text: ENUM_ISSUE_SUMMARY.SWITCHED_OFF_UNABLE_TO_CONTACT_RIDER,
    value: ENUM_ISSUE_SUMMARY.SWITCHED_OFF_UNABLE_TO_CONTACT_RIDER,
    show: [ENUM_ISSUE_DESTINATION.LOGISTICS]
  },
  {
    text: ENUM_ISSUE_SUMMARY.DUPLICATE_ORDER_CREATED,
    value: ENUM_ISSUE_SUMMARY.DUPLICATE_ORDER_CREATED,
    show: [ENUM_ISSUE_DESTINATION.TECH_TEAM]
  },
  {
    text: ENUM_ISSUE_SUMMARY.PACKAGING_ISSUES_KITCHEN,
    value: ENUM_ISSUE_SUMMARY.PACKAGING_ISSUES_KITCHEN,
    show: [ENUM_ISSUE_DESTINATION.KITCHEN]
  },
  {
    text: ENUM_ISSUE_SUMMARY.ORDER_CANCELLATION_REQUEST,
    value: ENUM_ISSUE_SUMMARY.ORDER_CANCELLATION_REQUEST,
    show: [
      ENUM_ISSUE_DESTINATION.KITCHEN,
      ENUM_ISSUE_DESTINATION.LOGISTICS,
      ENUM_ISSUE_DESTINATION.CUSTOMER_SERVICE,
      ENUM_ISSUE_DESTINATION.HOAX
    ]
  },
  {
    text: ENUM_ISSUE_SUMMARY.MANUAL_CONFIRMATION_PAYMENT_RECEIVED_CARD_NIP,
    value: ENUM_ISSUE_SUMMARY.MANUAL_CONFIRMATION_PAYMENT_RECEIVED_CARD_NIP,
    show: [ENUM_ISSUE_DESTINATION.BANK_AND_FINANCE]
  },
  {
    text: ENUM_ISSUE_SUMMARY.MEAL_MODIFICATION,
    value: ENUM_ISSUE_SUMMARY.MEAL_MODIFICATION,
    show: [ENUM_ISSUE_DESTINATION.KITCHEN]
  },
  {
    text: ENUM_ISSUE_SUMMARY.MEAL_CUSTOMIZATION,
    value: ENUM_ISSUE_SUMMARY.MEAL_CUSTOMIZATION,
    show: [
      ENUM_ISSUE_DESTINATION.KITCHEN,
      ENUM_ISSUE_DESTINATION.TECH_TEAM,
      ENUM_ISSUE_DESTINATION.BANK_AND_FINANCE
    ]
  },
  {
    text: ENUM_ISSUE_SUMMARY.MEAL_REPLACEMENT,
    value: ENUM_ISSUE_SUMMARY.MEAL_REPLACEMENT,
    show: [ENUM_ISSUE_DESTINATION.KITCHEN, ENUM_ISSUE_DESTINATION.LOGISTICS]
  },
  {
    text: ENUM_ISSUE_SUMMARY.SWITCH_TO_PICK_UP,
    value: ENUM_ISSUE_SUMMARY.SWITCH_TO_PICK_UP,
    show: [ENUM_ISSUE_DESTINATION.LOGISTICS]
  },
  {
    text: ENUM_ISSUE_SUMMARY.SWITCH_TO_DELIVERY,
    value: ENUM_ISSUE_SUMMARY.SWITCH_TO_DELIVERY,
    show: [ENUM_ISSUE_DESTINATION.LOGISTICS]
  },
  {
    text: ENUM_ISSUE_SUMMARY.FREE_DELIVERY,
    value: ENUM_ISSUE_SUMMARY.FREE_DELIVERY,
    show: [ENUM_ISSUE_DESTINATION.LOGISTICS]
  },
  {
    text: ENUM_ISSUE_SUMMARY.PAIR_ORDER_REQUEST,
    value: ENUM_ISSUE_SUMMARY.PAIR_ORDER_REQUEST,
    show: [ENUM_ISSUE_DESTINATION.LOGISTICS]
  },
  {
    text: ENUM_ISSUE_SUMMARY.SUMMARY_OTHER,
    value: ENUM_ISSUE_SUMMARY.SUMMARY_OTHER,
    show: []
  }
];

const CreateIssue: FC<Props> = () => {
  const EMPTY_FORM_DATA: FormData = {
    user_id: '',
    order_code: '',
    channel: '' as ENUM_ESCALATE_ITEMS,
    feedback_destination: '' as ENUM_ISSUE_DESTINATION,
    feedback_destination_other: '',
    feedback_summary: '' as ENUM_ISSUE_SUMMARY,
    resolution: '' as ENUM_ISSUE_RESOLUTION,
    case_summary: '',
    brands: []
  };

  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM_DATA);
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [selectedRider, setSelectedRider] = useState('');
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [orders, setOrders] = useState<OrderType[]>([]);
  const [users, setUsers] = useState<UserType[]>([]);

  const [riders, setRiders] = useState<RiderType[]>([]);
  const [ridersLoading, setRidersLoading] = useState(false);
  const [showChannelOptions, setShowChannelOptions] = useState(false);
  const [showSummaryOptions, setShowSummaryOptions] = useState(false);
  const [showDestinationOptions, setShowDestinationOptions] = useState(false);
  const [showResolutionOptions, setShowResolutionOptions] = useState(false);
  const { activeCokitchen } = useDashboardContext();
  const [brandOptionsList, setBrandOptionsList] = useState<MultiselectItem[]>([]);
  const [errors, setErrors] = useState({
    kitchen_feedback_destination: 'Brand selection is required',
    order_code: ''
  });
  const orderCodeOptions = useMemo(() => {
    return orders.map(({ order_code, user }) => ({
      value: order_code,
      text: `${order_code?.toUpperCase()} by ${getUserName(user)}`
    }));
  }, [orders]);
  const userCodeOptions = useMemo(() => {
    return users.map(({ first_name, last_name, id }) => ({
      value: id,
      text: `${first_name} ${last_name}`
    }));
  }, [users]);
  const userPhoneOptions = useMemo(() => {
    return users.map(({ first_name, last_name, phone_number, id }) => ({
      value: id,
      text: `${first_name} ${last_name} - ${phone_number}`
    }));
  }, [users]);

  const brandOptions = async () => {
    try {
      const res: any = await apiService.get('v2/brand_management/brands', {
        params: {
          cokitchen_id: activeCokitchen?.id,
          live: true,
          item_type: ENUM_ITEM_TYPE.FOOD,
          brand_type: ENUM_ORDER_PROVIDER.foodcourt
        },
        baseUrl: process.env.REACT_APP_BRAND_MGT_API_URL
      });
      setBrandOptionsList(
        res?.data?.data?.map((brand: any) => ({
          value: brand.id,
          text: brand.name
        }))
      );
    } catch {
      console.log('error');
    }
  };
  useEffect(() => {
    if (
      formData.feedback_destination === ENUM_ISSUE_DESTINATION.KITCHEN &&
      (selectedBrands?.length === 0 || !selectedBrands)
    ) {
      setErrors({
        ...errors,
        kitchen_feedback_destination: 'Brand selection is required'
      });
    } else {
      setErrors({
        ...errors,
        kitchen_feedback_destination: ''
      });
    }
    if (!formData?.order_code && !formData?.user_id) {
      setErrors({ ...errors, order_code: 'Order code, username, or phone number is required*' });
    }
  }, [formData, selectedBrands]);

  const riderOptions = useMemo(() => {
    const order = orders?.find((o) => o.order_code === formData.order_code);
    const options = riders.map((rider) => ({
      value: rider?.id,
      text: getUserName(rider)
    }));
    if (order?.rider) {
      setSelectedRider(order.rider.id);
      if (!options.find((o) => o.value === order.rider.id))
        return [{ value: order.rider.id, text: getUserName(order.rider) }, ...options];
    } else setSelectedRider('');
    return options;
  }, [orders, riders, formData.order_code]);

  const loadOrders = async (orderCode: string) => {
    if (orderCode?.length < 4 || orderCode?.length > 6) return;
    try {
      setLoadingOptions(true);
      const res = await apiService.get(`logistics/search/order?search=${orderCode}&page_no=1`, {
        baseUrl: process.env.REACT_APP_ORDER_STATS_API_KEY
      });
      setOrders(res.data?.data || []);
    } catch (error) {
      setOrders([]);
    } finally {
      setLoadingOptions(false);
    }
  };

  const loadUsers = async (value: string, filter_type: string) => {
    try {
      setLoadingOptions(true);
      const res = await apiService.get(`cs/search/keyword`, {
        params: { filter_value: value, filter_type: filter_type, page: 1 },
        baseUrl: process.env.REACT_APP_ORDER_STATS_API_KEY
      });
      setUsers(res.data?.data || []);
    } catch (error) {
      setUsers([]);
    } finally {
      setLoadingOptions(false);
    }
  };
  const loadRiders = async () => {
    try {
      setRidersLoading(true);
      const res = await apiService.get('logistics/riders/active', {
        baseUrl: process.env.REACT_APP_ORDER_STATS_API_KEY
      });
      setRiders(res.data?.riders ?? []);
    } catch (error) {
      setRiders([]);
    } finally {
      setRidersLoading(false);
    }
  };

  const updateOrderCode = (order_code: string) => {
    setFormData((data) => ({ ...data, order_code }));
    setErrors({ ...errors, order_code: '' });
  };
  const updateUserName = (user_id: string) => {
    setFormData((data) => ({ ...data, user_id }));
    setErrors({ ...errors, order_code: '' });
  };

  const updateDestination = (feedback_destination: string) => {
    if (feedback_destination !== ENUM_ISSUE_DESTINATION.DESTINATION_OTHER) {
      setFormData((data) => ({ ...data, feedback_destination, feedback_destination_other: '' }));
      setShowDestinationOptions(false);
    } else setFormData((data) => ({ ...data, feedback_destination }));
    setSelectedBrands([]);
  };

  const updateSummary = (feedback_summary: string) => {
    if (feedback_summary !== ENUM_ISSUE_SUMMARY.SUMMARY_OTHER) {
      setFormData((data) => ({ ...data, feedback_summary, feedback_summary_other: '' }));
      setShowSummaryOptions(false);
    } else setFormData((data) => ({ ...data, feedback_summary }));
  };

  const updateResolution = (resolution: string) => {
    if (resolution !== ENUM_ISSUE_RESOLUTION.RESOLUTION_OTHER) {
      setFormData((data) => ({ ...data, resolution, resolution_other: '' }));
      setShowResolutionOptions(false);
    } else setFormData((data) => ({ ...data, resolution }));
  };

  const createIssue = async () => {
    try {
      setLoading(true);
      const feedback_destination =
        formData.feedback_destination_other || formData.feedback_destination;
      const feedback_summary = formData.feedback_summary_other || formData.feedback_summary;
      const resolution = formData.resolution_other || formData.resolution;
      const data: FormData = {
        order_code: formData.order_code ? formData.order_code : undefined,
        user_id: formData.user_id ? formData.user_id : undefined,
        channel: formData.channel.toUpperCase(),
        feedback_destination: feedback_destination.toUpperCase(),
        feedback_summary: feedback_summary.toUpperCase()
      };
      if (resolution) data.resolution = resolution.toUpperCase();
      if (formData.case_summary) data.case_summary = formData.case_summary;
      if (selectedBrands?.length)
        data.brands = brandOptionsList
          ?.filter((b) => selectedBrands.includes(b.value))
          ?.map((b) => ({ id: b.value, name: b.text }));
      if (selectedRider) data.rider_id = selectedRider;
      await apiService.post('cs/issue/add', {
        baseUrl: process.env.REACT_APP_ORDER_STATS_API_KEY,
        data
      });

      toast(<ToastMessage type='success' message='Issue created successful created' />);
      setShow(false);
    } catch {
      toast(<ToastMessage type='error' message='Error creating issue, please try again later' />);
    } finally {
      setLoading(false);
    }
  };

  const searchOrdersByCode = (orderCode: string) => {
    if (orderCode?.length < 4 || orderCode?.length > 6) return;

    const minifiedOrderCode = orderCode.split(' ')[0].toLowerCase();
    if (orderCode && minifiedOrderCode !== formData.order_code) {
      loadOrders(orderCode);
    }
  };
  const searchOrdersByName = (username: string) => {
    if (username === '') return;
    loadUsers(username, 'full_name');
  };

  const searchOrdersByPhone = (phone_number: string) => {
    if (phone_number === '') return;
    loadUsers(phone_number, 'phone_number');
  };
  useEffect(() => {
    if (show) {
      setFormData(EMPTY_FORM_DATA);
      setLoading(false);
      loadRiders();
      brandOptions();
    }
  }, [show]);

  const validator =
    (!formData.order_code && !formData.user_id) ||
    !formData.channel ||
    (formData.feedback_destination === ENUM_ISSUE_DESTINATION.KITCHEN &&
      selectedBrands.length === 0) ||
    (!formData.feedback_destination && !formData.feedback_destination_other) ||
    (!formData.feedback_summary && !formData.feedback_summary_other);

  return (
    <Fragment>
      <Button
        small
        onClick={() => setShow(true)}
        color='primary'
        variant='block'
        className='w-[107px] px-3 text-sm h-[33px]'>
        <AddIcon className='w-4 h-4' />
        <span> Task </span>
      </Button>
      <BaseModal
        isOpen={show}
        onRequestClose={() => setShow(false)}
        title='Log new task'
        titleIcon={<NotificationIcon className='text-blue-5' />}
        titleIconColor='info'
        footer={
          <Button
            color='primary'
            variant='block'
            small
            disabled={validator}
            loading={loading}
            onClick={createIssue}>
            Confirm
          </Button>
        }>
        <div className='flex flex-col gap-[20px]'>
          <div>
            <p className='font-semibold text-xs text-red-5 capitalize mb-1'>{errors.order_code}</p>

            <p className='font-semibold text-[14px] capitalize mb-1'>Order Code</p>
            <Multiselect
              hasSearch
              placeholder='Start typing order code...'
              values={formData.order_code ? [formData.order_code] : []}
              options={orderCodeOptions}
              onChange={(e) => updateOrderCode(e[0])}
              isSingle
              onSearchChange={searchOrdersByCode}
              loading={loadingOptions}
            />
          </div>
          <div>
            <p className='font-semibold text-[14px] capitalize mb-1'>User Full Name</p>
            <Multiselect
              hasSearch
              placeholder='Input user full name ...'
              values={formData.user_id ? [formData.user_id] : []}
              options={userCodeOptions}
              onChange={(e) => updateUserName(e[0])}
              isSingle
              onSearchChange={searchOrdersByName}
              loading={loadingOptions}
            />
          </div>

          <div>
            <p className='font-semibold text-[14px] capitalize mb-1'>User Phone Number</p>
            <Multiselect
              hasSearch
              placeholder='Input user phone number ...'
              values={formData.user_id ? [formData.user_id] : []}
              options={userPhoneOptions}
              onChange={(e) => updateUserName(e[0])}
              isSingle
              onSearchChange={searchOrdersByPhone}
              loading={loadingOptions}
            />
          </div>

          <div>
            <p className='font-semibold text-[14px] capitalize mb-1'>Channel of escalation</p>
            <Dropdown
              visible={showChannelOptions}
              onClickOutside={() => setShowChannelOptions(false)}
              content={
                <div
                  className='px-5 h-[62px] py-3 flex items-center justify-between text-sm gap-3 font-medium w-full border-[1px] border-[#E0DADB] rounded-lg cursor-pointer'
                  onClick={() => setShowChannelOptions(!showChannelOptions)}>
                  {formData.channel ? formData.channel : 'Select Channel'}
                  <ArrowDownIcon />
                </div>
              }>
              <DropdownList className='w-[200px]'>
                {CHANNEL_OF_ESCALATION.map((item, index) => (
                  <DropdownItem
                    key={index}
                    onClick={() => {
                      setFormData((data) => ({ ...data, channel: item.value }));
                      setShowChannelOptions(false);
                    }}>
                    {item.text}
                  </DropdownItem>
                ))}
              </DropdownList>
            </Dropdown>
          </div>

          <div>
            <p className='font-semibold text-[14px] capitalize mb-1'>Feedback destination</p>
            <Dropdown
              visible={showDestinationOptions}
              onClickOutside={() => setShowDestinationOptions(false)}
              content={
                <div
                  className='px-5 h-[62px] py-3 flex items-center justify-between text-sm gap-3 font-medium w-full border-[1px] border-[#E0DADB] rounded-lg cursor-pointer'
                  onClick={() => setShowDestinationOptions(!showDestinationOptions)}>
                  {formData.feedback_destination || formData.feedback_destination_other
                    ? formData.feedback_destination || formData.feedback_destination_other
                    : 'Select Destination'}
                  <ArrowDownIcon />
                </div>
              }>
              <DropdownList className='w-[200px]'>
                {FEEDBACK_DESTINATION.map((item, index) => (
                  <DropdownItem key={index} onClick={() => updateDestination(item.value)}>
                    {item.text}
                  </DropdownItem>
                ))}
                {formData.feedback_destination === ENUM_ISSUE_DESTINATION.DESTINATION_OTHER ? (
                  <Textarea
                    autoFocus
                    name='destination_other'
                    className='text-[14px]'
                    placeholder='type here...'
                    onChange={(e) =>
                      setFormData({ ...formData, feedback_destination_other: e.target.value })
                    }
                    value={formData.feedback_destination_other}
                  />
                ) : null}
              </DropdownList>
            </Dropdown>
          </div>
          {formData.feedback_destination === ENUM_ISSUE_DESTINATION.KITCHEN ? (
            <div>
              <p className='font-semibold text-[14px] capitalize mb-1'>Brands</p>
              <Multiselect
                placeholder='Select one or more brands'
                options={brandOptionsList}
                values={selectedBrands}
                onChange={(v) => setSelectedBrands(v)}
              />

              <p className='font-semibold text-xs text-red-5 capitalize mb-1'>
                {errors.kitchen_feedback_destination}
              </p>
            </div>
          ) : null}
          {formData.feedback_destination === ENUM_ISSUE_DESTINATION.LOGISTICS ? (
            <div>
              <p className='font-semibold text-[14px] capitalize mb-1'>Rider (Optional)</p>
              <Multiselect
                hasSearch
                isSingle
                filterResults
                placeholder='Select rider'
                options={riderOptions}
                values={selectedRider ? [selectedRider] : []}
                onChange={(v) => setSelectedRider(v[0])}
                loading={ridersLoading}
              />
              {}
            </div>
          ) : null}
          <div>
            <p className='font-semibold text-[14px] capitalize mb-1'>Actual Issue</p>
            <Dropdown
              visible={showSummaryOptions}
              onClickOutside={() => setShowSummaryOptions(false)}
              content={
                <div
                  className='px-5 h-[62px] py-3 flex items-center cursor-pointer justify-between text-sm gap-3 font-medium w-full border-[1px] border-[#E0DADB] rounded-lg'
                  onClick={() => setShowSummaryOptions(!showSummaryOptions)}>
                  {formData.feedback_summary || formData.feedback_summary_other
                    ? formData.feedback_summary || formData.feedback_summary_other
                    : 'Select Actual Issue'}
                  <ArrowDownIcon />
                </div>
              }>
              <DropdownList className='w-[200px]'>
                {ISSUE_SUMMARY.filter((item) =>
                  item.show.includes(formData.feedback_destination as ENUM_ISSUE_DESTINATION)
                ).map((item, index) => (
                  <DropdownItem key={index} onClick={() => updateSummary(item.value)}>
                    {item.text}
                  </DropdownItem>
                ))}
                {formData.feedback_summary === ENUM_ISSUE_SUMMARY.SUMMARY_OTHER ? (
                  <Textarea
                    autoFocus
                    name='summary_other'
                    className='text-[14px]'
                    placeholder='type here...'
                    onChange={(e) =>
                      setFormData({ ...formData, feedback_summary_other: e.target.value })
                    }
                    value={formData.feedback_summary_other}
                  />
                ) : null}
              </DropdownList>
            </Dropdown>
          </div>
          <div>
            <p className='font-semibold text-[14px] capitalize mb-1'>Case Summary (optional)</p>
            <Textarea
              name='case_summary'
              placeholder='Case Summary'
              value={formData?.case_summary}
              onChange={(e) => setFormData({ ...formData, case_summary: e.target.value })}
            />
          </div>
          <div>
            <p className='font-semibold text-[14px] capitalize mb-1'>Resolution (optional)</p>
            <Dropdown
              visible={showResolutionOptions}
              onClickOutside={() => setShowResolutionOptions(false)}
              content={
                <div
                  className='px-5 h-[62px] py-3 flex items-center justify-between text-sm gap-3 font-medium w-full border-[1px] border-[#E0DADB] rounded-lg cursor-pointer'
                  onClick={() => setShowResolutionOptions(!showResolutionOptions)}>
                  {formData.resolution || formData.resolution_other
                    ? formData.resolution || formData.resolution_other
                    : 'Select Resolution'}
                  <ArrowDownIcon />
                </div>
              }>
              <DropdownList className='w-[200px]'>
                {ISSUE_RESOLUTION.map((item, index) => (
                  <DropdownItem key={index} onClick={() => updateResolution(item.value)}>
                    {item.text}
                  </DropdownItem>
                ))}
                {formData.resolution === ENUM_ISSUE_RESOLUTION.RESOLUTION_OTHER ? (
                  <Textarea
                    autoFocus
                    name='resolution_other'
                    className='text-[14px]'
                    placeholder='type here...'
                    onChange={(e) => setFormData({ ...formData, resolution_other: e.target.value })}
                    value={formData.resolution_other}
                  />
                ) : null}
              </DropdownList>
            </Dropdown>
          </div>
        </div>
      </BaseModal>
    </Fragment>
  );
};
export default CreateIssue;
